import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CartSidebar from '../../components/feature/CartSidebar';
import SearchModal from '../../components/feature/SearchModal';
import LoginModal from '../../components/feature/LoginModal';
import { useProduct, useRelatedProducts } from '../../hooks/useProducts';
import { useCart } from '../../contexts/CartContext';
import ProductImageGallery from './components/ProductImageGallery';
import ProductInfo from './components/ProductInfo';
import ProductTabs from './components/ProductTabs';
import RelatedProducts from './components/RelatedProducts';

export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { addItem } = useCart();
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [stickyAdded, setStickyAdded] = useState(false);

  const { product, loading } = useProduct(slug || '');
  const related = useRelatedProducts(product?.category_name || '', product?.id || '');

  const discount = product?.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : null;

  useEffect(() => {
    const onScroll = () => setStickyVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleStickyAddToCart = () => {
    if (!product) return;
    addItem({
      id: product.id,
      product_id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url,
      slug: product.slug,
      weight: product.weight,
    });
    setStickyAdded(true);
    setTimeout(() => setStickyAdded(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-light">
        <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />
        <main className="pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
              <div className="aspect-square bg-cream rounded-2xl" />
              <div className="space-y-4 py-4">
                <div className="h-3 bg-cream rounded w-1/4" />
                <div className="h-9 bg-cream rounded w-3/4" />
                <div className="h-3 bg-cream rounded w-1/3" />
                <div className="h-24 bg-cream rounded mt-4" />
                <div className="h-12 bg-cream rounded w-2/3 mt-4" />
                <div className="h-12 bg-cream rounded" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
        <CartSidebar />
        <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
        <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-cream-light">
        <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />
        <main className="pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center py-24">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-cream rounded-full">
              <i className="ri-inbox-line text-4xl text-coffee-light" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-coffee mb-3">Ürün Bulunamadı</h2>
            <p className="text-coffee-light mb-8">Aradığınız ürün mevcut değil veya kaldırılmış olabilir.</p>
            <Link
              to="/urunler"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-coffee text-white font-semibold rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Ürünlere Dön
            </Link>
          </div>
        </main>
        <Footer />
        <CartSidebar />
        <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
        <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
      </div>
    );
  }

  const allImages = [product.image_url, ...product.galeri_gorseller].filter(Boolean);

  return (
    <div className="min-h-screen bg-cream-light font-sans">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />

      {/* Sticky Add to Cart Bar */}
      <div className={`fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-cream-dark transition-transform duration-300 ${stickyVisible ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-cream flex-shrink-0">
            {product.image_url && (
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover object-top" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-coffee truncate">{product.name}</p>
            <p className="text-base font-bold text-coffee">{product.price.toLocaleString('tr-TR')}₺</p>
          </div>
          <button
            onClick={handleStickyAddToCart}
            disabled={product.stock === 0}
            className={`px-6 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 ${
              stickyAdded ? 'bg-green-500 text-white' : 'bg-coffee hover:bg-coffee-medium text-white'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`text-sm ${stickyAdded ? 'ri-check-line' : 'ri-shopping-bag-line'}`} />
            </div>
            {stickyAdded ? 'Eklendi!' : 'Sepete Ekle'}
          </button>
        </div>
      </div>

      <main className="pt-24 pb-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-coffee-light mb-8 flex-wrap">
            <Link to="/" className="hover:text-gold transition-colors">Ana Sayfa</Link>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </div>
            <Link to="/urunler" className="hover:text-gold transition-colors">Ürünler</Link>
            {product.category_name && (
              <>
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-sm" />
                </div>
                <Link
                  to={`/urunler?kategori=${encodeURIComponent(product.category_name)}`}
                  className="hover:text-gold transition-colors"
                >
                  {product.category_name}
                </Link>
              </>
            )}
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </div>
            <span className="text-coffee font-medium truncate max-w-[200px]">{product.name}</span>
          </nav>

          {/* Main Product Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-12 mb-16">
            {/* Left: Images */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <ProductImageGallery
                images={allImages}
                productName={product.name}
                badge={product.badge}
                discount={discount}
              />
            </div>

            {/* Right: Info */}
            <div>
              <ProductInfo product={product} />
            </div>
          </div>

          {/* Tabs */}
          <ProductTabs product={product} />

          {/* Related Products */}
          <RelatedProducts products={related} />
        </div>
      </main>

      <Footer />
      <CartSidebar />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
